/**
 * Canonical links into conditions spot pages.
 *
 * Every spot has one indexable URL: /conditions/<slug>, or
 * /conditions/sailing/<slug> for sailing venues. The sport lens and the
 * session planner deep link travel in the URL fragment, which crawlers ignore,
 * so internal links never mint ?sport= duplicates of a spot page.
 * ConditionsFragmentLens (mounted by app/conditions/layout.tsx) applies the
 * fragment to the page after hydration.
 */

const SAILING_SPORT_KEYS = new Set(["sailing", "sailing-club", "sailing-hp"]);

/** A plain element id that may follow the lens in a fragment. */
export const SECTION_ANCHOR = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/u;

/**
 * Fragment keys the conditions layout understands. `area` filters a board to a
 * forecast area and `view=week` opens a board's This week view; both are applied
 * as shallow params after hydration, like `sport`.
 */
export const CONDITIONS_FRAGMENT_KEYS = ["sport", "planner", "area", "view"] as const;

type ConditionsFragmentKey = (typeof CONDITIONS_FRAGMENT_KEYS)[number];

export function isSailingSportKey(sport: string | null | undefined): boolean {
  return Boolean(sport && SAILING_SPORT_KEYS.has(sport));
}

export function conditionsSpotHref(
  slug: string,
  sport?: string | null,
  options: Readonly<{ planner?: boolean; anchor?: string }> = {},
): string {
  const parts: string[] = [];
  const sailing = isSailingSportKey(sport);
  if (sport && !sailing) parts.push(`sport=${encodeURIComponent(sport)}`);
  if (options.planner) parts.push("planner=1");
  // A bare section id (for example "season") goes last; the conditions layout
  // scrolls to it once the lens is applied.
  if (options.anchor && SECTION_ANCHOR.test(options.anchor)) parts.push(options.anchor);
  const base = sailing ? `/conditions/sailing/${slug}` : `/conditions/${slug}`;
  return parts.length > 0 ? `${base}#${parts.join("&")}` : base;
}

/**
 * A board link with an optional area filter or week view in the fragment, so
 * crawlers only see the canonical board path:
 * "/conditions/wind#area=cornwall", "/conditions/piste#view=week".
 */
export function conditionsBoardHref(
  boardPath: string,
  options: Readonly<{ area?: string; view?: "week" }> = {},
): string {
  const parts: string[] = [];
  if (options.area && /^[a-z0-9-]{1,40}$/u.test(options.area)) parts.push(`area=${options.area}`);
  if (options.view === "week") parts.push("view=week");
  return parts.length > 0 ? `${boardPath}#${parts.join("&")}` : boardPath;
}

export type ConditionsFragment = Readonly<{
  /** Values to apply as shallow search params. */
  params: Readonly<Partial<Record<ConditionsFragmentKey, string>>>;
  /** Whatever else was in the fragment, without the leading "#". */
  rest: string;
}>;

/**
 * Reads a location hash such as "#sport=wind&planner=1". Returns null when the
 * hash carries no lens key, so ordinary in-page anchors are left alone.
 */
export function parseConditionsFragment(hash: string): ConditionsFragment | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw.includes("=")) return null;
  const params: Partial<Record<ConditionsFragmentKey, string>> = {};
  const rest: string[] = [];
  for (const part of raw.split("&")) {
    if (!part) continue;
    const separator = part.indexOf("=");
    const key = separator < 0 ? part : part.slice(0, separator);
    const lensKey = CONDITIONS_FRAGMENT_KEYS.find((candidate) => candidate === key);
    if (!lensKey) {
      rest.push(part);
      continue;
    }
    let value = "";
    try {
      value = decodeURIComponent(part.slice(separator + 1).replace(/\+/gu, " "));
    } catch {
      value = "";
    }
    // `view` is a lens only for the board week view; other values (#view=map) stay anchors.
    if (lensKey === "view" && value !== "week") {
      rest.push(part);
      continue;
    }
    if (separator >= 0 && /^[a-z0-9-]{1,40}$/u.test(value)) params[lensKey] = value;
  }
  if (Object.keys(params).length === 0) return null;
  return { params, rest: rest.join("&") };
}

/** The section id left in a fragment after the lens, if it is a plain id. */
export function fragmentSectionAnchor(rest: string): string | null {
  return SECTION_ANCHOR.test(rest) ? rest : null;
}
